import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import React, { useContext, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import {
  EDIT_PAGE_CONNECTION_ORDER_CREATE_NO_ORDER_ID_ERROR,
  PAGE_VALIDATION_ERROR,
} from 'src/app/constants/errorMessages';
import { ROUTE_EDIT_PAY_AND_POST } from 'src/app/constants/routes';
import { FEATURE_BRANDING, useFeatureToggleData } from 'src/app/featureToggle';
import { isAggregatedAd } from 'src/app/utils/isAggregatedAd';
import { log } from 'src/app/utils/logging';
import { EditPageActions } from 'src/app/views/components/EditPageActions';
import { DiscardDialogContext } from 'src/app/views/context/DiscardDialogContext';
import { EditFlowSectionsManagerContext } from 'src/app/views/context/EditFlowSectionsManagerContext';
import { EditFlowDataContext } from 'src/app/views/context/cajaFlowData';
import { useEditSubmitOrder } from 'src/app/views/hooks/useEditSubmitOrder';
import { useErrorType } from 'src/app/views/hooks/useErrorType';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import { usePrivateAdFeature } from 'src/app/views/hooks/usePrivateAdFeature';
import { BasicPageLayout } from 'src/app/views/layouts/BasicPageLayout';
import type { AdProductUpgrade } from 'src/graphql/generated';
import type AppState from 'src/types/appState';

import translations from './.vocab';
import { BrandingSection } from './components/BrandingSection';
import { CandidateSearchResultsSection } from './components/CandidateSearchResultsSection';
import { JobDescriptionSection } from './components/JobDescriptionSection';
import { ManageCandidateApplicationsSection } from './components/ManageCandidateApplicationsSection';
import { PayDetailsSection } from './components/PayDetailsSection';
import { PrivateAdSection } from './components/PrivateAdSection';
import { RoleInformationSection } from './components/RoleInformationSection';
import { StandOutUpsellSection } from './components/StandoutUpsellSection';

export const EditPage = () => {
  const {
    advertiserNation,
    editFlowCheckout,
    productSet,
    seekAdvertiserId,
    postChannel,
  } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      editFlowCheckout: state.session.editFlowCheckout,
      productSet: state.session.productSet,
      seekAdvertiserId: state.session.seekAdvertiserId,
      postChannel: state.session.postChannel,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const { loading: isEditDataLoading } = useContext(EditFlowDataContext);
  const { sectionsManager, sectionRefs } = useContext(
    EditFlowSectionsManagerContext,
  );
  const { openDialog: openDiscardDialog } = useContext(DiscardDialogContext);

  const { errorType, resetError, setError } = useErrorType();
  const {
    goToRoute,
    redirectToJobManagement,
    redirectToCandidateJobDetailsPage,
  } = useLocalisedRoutes();
  const { saveDraftAndCreateOrder, confirmOrderAndCloseDraft } =
    useEditSubmitOrder();

  const upgradeItems = (productSet?.items as Array<AdProductUpgrade>)?.filter(
    (item) =>
      item && item.features.some((feature) => feature?.type !== 'JobAdEdit'),
  ) as AdProductUpgrade[] | undefined;

  useEffect(() => {
    /**
     * Aside from JobAdEdit, there should only be 1 upgrade-able product.
     * In ANZ, this is BrandedUpgrade
     * Log a warning if there is ever more than 1 item (exclude JobAdEdit) returned
     */
    if (upgradeItems && upgradeItems.length > 1) {
      log.warn('Edit | upgradeItems | There is more than 1 upgrade-able item', {
        advertiserNation,
        seekAdvertiserId,
        upgradeItems,
        productSet,
      });
    }
  }, [advertiserNation, upgradeItems, productSet, seekAdvertiserId]);

  const redirect = (referrer?: string): void => {
    if (isAggregatedAd(postChannel)) {
      redirectToCandidateJobDetailsPage();
    } else {
      redirectToJobManagement(referrer);
    }
  };

  const onPublish = async () => {
    if (editFlowCheckout.loading) {
      return;
    }

    const isValid = await sectionsManager.validateSections();

    if (!isValid) {
      throw new Error(PAGE_VALIDATION_ERROR);
    }

    const { orderId } = await saveDraftAndCreateOrder();

    if (!orderId) {
      throw new Error(EDIT_PAGE_CONNECTION_ORDER_CREATE_NO_ORDER_ID_ERROR);
    }

    if (editFlowCheckout.enabled) {
      goToRoute(ROUTE_EDIT_PAY_AND_POST);
      return;
    }

    await confirmOrderAndCloseDraft(orderId);

    sectionsManager.resetSections();

    redirect('edit-page-success');
  };

  const onCancel = () => {
    if (
      sectionsManager.hasSectionsInEditMode() ||
      sectionsManager.hasSectionsWithSavedChanges()
    ) {
      openDiscardDialog();
      return;
    }

    redirect();
  };

  const isBrandingEnabled = useFeatureToggleData(FEATURE_BRANDING);
  const { showPrivateAdToggle, showPhoneNumber } = usePrivateAdFeature();

  return (
    <BasicPageLayout title={t('edit-page-title')} errorType={errorType}>
      <Stack space="medium">
        <Stack space="medium">
          <RoleInformationSection ref={sectionRefs.roleInformationSectionRef} />

          <PayDetailsSection ref={sectionRefs.payDetailsSectionRef} />

          {(showPrivateAdToggle || showPhoneNumber) && !isEditDataLoading && (
            <PrivateAdSection ref={sectionRefs.privateAdSectionRef} />
          )}

          {isBrandingEnabled && !isEditDataLoading && (
            <BrandingSection ref={sectionRefs.brandingSectionRef} />
          )}

          <JobDescriptionSection ref={sectionRefs.jobDescriptionSectionRef} />

          <CandidateSearchResultsSection
            ref={sectionRefs.candidateSearchResultsSectionRef}
          />

          <ManageCandidateApplicationsSection
            ref={sectionRefs.manageCandidateApplicationsSectionRef}
          />

          {upgradeItems && upgradeItems.length > 0 && !isEditDataLoading && (
            <StandOutUpsellSection
              ref={sectionRefs.standOutUpsellSectionRef}
              upgradeItem={upgradeItems[0]}
            />
          )}
        </Stack>

        <EditPageActions
          onPublish={onPublish}
          onCancel={onCancel}
          onError={(error) => setError(error)}
          resetError={resetError}
        />
      </Stack>
    </BasicPageLayout>
  );
};
